import Keycloak from 'keycloak-js';

/** Realm role a user needs to see the dashboard (mirrors KEYCLOAK_REQUIRED_ROLE on the API). */
export const REQUIRED_ROLE = 'user';

/**
 * Authentication is strictly controlled via environment variable VITE_AUTH_ENABLED.
 * - Set VITE_AUTH_ENABLED=true in .env to enforce Keycloak login.
 * - Set VITE_AUTH_ENABLED=false (or leave unset in local dev) to bypass login.
 */
export const isAuthEnabled = (): boolean => {
  return import.meta.env.VITE_AUTH_ENABLED === 'true';
};

export const keycloak = isAuthEnabled()
  ? new Keycloak({
      url: import.meta.env.VITE_KEYCLOAK_URL ?? 'http://localhost:8081/auth',
      realm: import.meta.env.VITE_KEYCLOAK_REALM ?? 'upbeat',
      clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID ?? 'upbeat-web',
    })
  : null;

/**
 * Runs once before the app renders: redirects to the Keycloak login page when enabled.
 * If auth is disabled, resolves immediately to allow direct access.
 */
export async function initAuth(): Promise<boolean> {
  if (!isAuthEnabled() || !keycloak) {
    return true;
  }
  try {
    return await keycloak.init({ onLoad: 'login-required', pkceMethod: 'S256', checkLoginIframe: false });
  } catch (err) {
    console.warn('Keycloak auth bypassed due to connection error:', err);
    return true;
  }
}

/** A fresh access token, refreshed when it expires within 30 s; returns empty if auth is disabled. */
export async function getToken(): Promise<string> {
  if (!isAuthEnabled() || !keycloak) {
    return '';
  }
  try {
    await keycloak.updateToken(30);
    return keycloak.token ?? '';
  } catch {
    return '';
  }
}

export const hasRequiredRole = () => {
  if (!isAuthEnabled() || !keycloak) {
    return true;
  }
  return keycloak.hasRealmRole(REQUIRED_ROLE);
};

export const username = () => {
  if (!isAuthEnabled() || !keycloak) {
    return 'demo';
  }
  return (keycloak.tokenParsed?.preferred_username as string | undefined) ?? keycloak.tokenParsed?.sub ?? '';
};

export const logout = () => {
  if (!isAuthEnabled() || !keycloak) {
    window.location.reload();
    return;
  }
  keycloak.logout({ redirectUri: window.location.origin + import.meta.env.BASE_URL });
};
